import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { NotificationsToggle } from '../components/NotificationsToggle';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  Wrench,
  Calendar,
  Package,
  Users,
  ClipboardList,
  QrCode,
  MessageSquare,
  Bell,
  FileText,
  Building2,
  AlertCircle,
  Inbox,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  MailWarning
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatLKR = (n) => `LKR ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [breakdown, setBreakdown] = useState({
    by_property: [],
    by_resident: [],
    total_outstanding: 0,
    total_maintenance_outstanding: 0,
    total_water_outstanding: 0,
    total_common_water_outstanding: 0,
    total_common_electricity_outstanding: 0,
  });
  // Which property rows are expanded in "Outstanding by Property" (drill-down
  // showing per-resident contribution to that property's outstanding amounts).
  const [expandedProperties, setExpandedProperties] = useState(() => new Set());
  // Per-property flag to show all contributing residents (default caps at 5).
  const [showAllResidentsFor, setShowAllResidentsFor] = useState(() => new Set());
  const DRILLDOWN_ROW_CAP = 5;
  const togglePropertyExpand = (pid) => {
    setExpandedProperties((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };
  const expandAllProperties = () => {
    setExpandedProperties(new Set(breakdown.by_property.map((p) => p.property_id)));
  };
  const collapseAllProperties = () => {
    setExpandedProperties(new Set());
    setShowAllResidentsFor(new Set());
  };
  const toggleShowAllResidents = (pid) => {
    setShowAllResidentsFor((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  // Management-only: open the "Preview recipients" modal. Fetches the
  // detailed list of overdue residents (name · property · overdue amount)
  // so the admin can uncheck anyone they don't want to email before sending.
  const openReminderModal = async () => {
    if (sendingReminders) return;
    if ((overdueSummary.overdue_resident_count || 0) === 0) {
      toast.info('No residents are currently overdue.');
      return;
    }
    setReminderModalOpen(true);
    setReminderLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/invoices/overdue-recipients`, { withCredentials: true });
      const recipients = data.recipients || [];
      setReminderRecipients(recipients);
      // Pre-select everyone with an email on file.
      setSelectedRecipients(new Set(
        recipients.filter((r) => r.has_email).map((r) => r.resident_id)
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load recipients');
      setReminderModalOpen(false);
    } finally {
      setReminderLoading(false);
    }
  };

  const toggleRecipient = (rid) => {
    setSelectedRecipients((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid); else next.add(rid);
      return next;
    });
  };
  const selectAllRecipients = () => {
    setSelectedRecipients(new Set(
      reminderRecipients.filter((r) => r.has_email).map((r) => r.resident_id)
    ));
  };
  const clearAllRecipients = () => setSelectedRecipients(new Set());

  const handleSendReminders = async () => {
    if (sendingReminders) return;
    if (selectedRecipients.size === 0) {
      toast.error('Select at least one recipient.');
      return;
    }
    setSendingReminders(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/invoices/send-overdue-reminders`,
        { resident_ids: Array.from(selectedRecipients) },
        { withCredentials: true }
      );
      const sent = data.sent?.length || 0;
      const failed = data.failed?.length || 0;
      const skipped = data.skipped_no_email?.length || 0;
      if (sent > 0 && failed === 0 && skipped === 0) {
        toast.success(`Reminder emails sent to ${sent} resident${sent === 1 ? '' : 's'}.`);
      } else if (sent > 0) {
        toast.success(
          `Sent ${sent}. ${failed ? `Failed ${failed}. ` : ''}${skipped ? `Skipped ${skipped} (no email on file).` : ''}`
        );
      } else {
        toast.error(
          `No reminders sent. ${failed ? `Failed ${failed}. ` : ''}${skipped ? `Skipped ${skipped} (no email on file).` : ''}`
        );
      }
      setReminderModalOpen(false);
      await loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };
  // Management-only: count of resident bank-transfer submissions awaiting
  // approval. Surfaced as a top-of-dashboard banner + Quick Action for
  // frictionless access from the primary landing screen.
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  // Management-only: count of residents whose invoices are past their due date.
  // Used to power the "Send Overdue Reminders" banner. Refreshed by `loadStats`.
  const [overdueSummary, setOverdueSummary] = useState({
    overdue_resident_count: 0,
    overdue_invoice_count: 0,
    total_overdue_amount: 0,
  });
  const [sendingReminders, setSendingReminders] = useState(false);
  // "Preview recipients" modal state — opens on click of the reminder button
  // so admins can uncheck specific residents before sending.
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [reminderRecipients, setReminderRecipients] = useState([]);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const loadStats = async () => {
    try {
      const requests = [axios.get(`${API}/dashboard/stats`, { withCredentials: true })];
      if (user?.role === 'management') {
        requests.push(axios.get(`${API}/dashboard/outstanding-breakdown`, { withCredentials: true }));
        requests.push(axios.get(`${API}/admin/payments/pending`, { withCredentials: true }));
        requests.push(axios.get(`${API}/admin/invoices/overdue-summary`, { withCredentials: true }));
      }
      const results = await Promise.all(requests);
      setStats(results[0].data);
      if (results[1]) setBreakdown(results[1].data);
      if (results[2]) setPendingApprovalsCount(Array.isArray(results[2].data) ? results[2].data.length : 0);
      if (results[3]) setOverdueSummary(results[3].data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getQuickActions = () => {
    switch (user?.role) {
      case 'resident':
        return [
          { label: 'Pay Maintenance Fee', icon: DollarSign, path: '/invoices', color: '#10b981' },
          { label: 'Book Facility', icon: Calendar, path: '/facilities', color: '#2563eb' },
          { label: 'Submit Request', icon: Wrench, path: '/maintenance', color: '#f59e0b' },
          { label: 'Generate Visitor Pass', icon: QrCode, path: '/visitors', color: '#8b5cf6' },
          { label: 'Track Parcels', icon: Package, path: '/parcels', color: '#ec4899' },
          { label: 'Announcements', icon: Bell, path: '/announcements', color: '#14b8a6' }
        ];
      case 'management':
        return [
          {
            label: pendingApprovalsCount > 0
              ? `Payment Approvals (${pendingApprovalsCount})`
              : 'Payment Approvals',
            icon: Inbox,
            path: '/payment-approvals',
            color: pendingApprovalsCount > 0 ? '#d97706' : '#64748b',
          },
          { label: 'Manage Users', icon: Users, path: '/users', color: '#0ea5e9' },
          { label: 'Manage Bookings', icon: Calendar, path: '/facilities', color: '#2563eb' },
          { label: 'View Requests', icon: ClipboardList, path: '/maintenance', color: '#f59e0b' },
          { label: 'Send Announcement', icon: Bell, path: '/announcements', color: '#8b5cf6' },
          { label: 'Monthly Report', icon: FileText, path: '/reports', color: '#10b981' }
        ];
      case 'security':
        return [
          { label: 'Scan Visitor Pass', icon: QrCode, path: '/visitors', color: '#8b5cf6' },
          { label: 'Log Parcel', icon: Package, path: '/parcels', color: '#ec4899' }
        ];
      case 'owner':
        return [
          { label: 'View Properties', icon: Users, path: '/properties', color: '#2563eb' },
          { label: 'Payment History', icon: DollarSign, path: '/payments', color: '#10b981' },
          { label: 'Reports', icon: FileText, path: '/reports', color: '#f59e0b' }
        ];
      default:
        return [];
    }
  };

  const getStatsCards = () => {
    if (user?.role === 'resident') {
      const pendingCount = stats.pending_payments_count || 0;
      const creditBalance = Number(user?.credit_balance || 0);
      const cards = [];
      // If the resident has a credit balance carried forward from an
      // overpayment, surface it prominently at the top so they can see the
      // running credit before the "pending payments" card.
      if (creditBalance > 0) {
        cards.push({
          label: 'Credit Balance',
          value: `LKR ${creditBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          sublabel: 'Auto-applied to your next invoices',
          icon: DollarSign,
          color: '#10b981',
        });
      }
      cards.push(
        {
          label: 'Pending Payments',
          value: `LKR ${(stats.pending_payments || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          sublabel: pendingCount > 0 ? `${pendingCount} unpaid invoice${pendingCount > 1 ? 's' : ''}` : 'All settled',
          icon: DollarSign,
          color: '#ef4444',
        },
        { label: 'Open Maintenance', value: stats.maintenance_requests || 0, icon: Wrench, color: '#f59e0b' },
        { label: 'Active Bookings', value: stats.active_bookings || 0, icon: Calendar, color: '#2563eb' },
        { label: 'Pending Parcels', value: stats.pending_parcels || 0, icon: Package, color: '#10b981' },
      );
      return cards;
    } else if (user?.role === 'management') {
      return [
        {
          label: 'Payment Approvals',
          value: pendingApprovalsCount,
          sublabel: pendingApprovalsCount > 0
            ? 'Tap Quick Action to review'
            : 'All caught up',
          icon: Inbox,
          color: pendingApprovalsCount > 0 ? '#d97706' : '#10b981',
        },
        { label: 'Total Residents', value: stats.total_residents || 0, icon: Users, color: '#2563eb' },
        { label: 'Pending Bookings', value: stats.pending_bookings || 0, icon: Calendar, color: '#f59e0b' },
        { label: 'Open Maintenance', value: stats.open_maintenance || 0, icon: Wrench, color: '#ef4444' },
        {
          label: 'Collections',
          value: `LKR ${(stats.total_collections || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          sublabel: (stats.outstanding_amount || 0) > 0
            ? `LKR ${(stats.outstanding_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} outstanding`
            : 'No outstanding',
          icon: DollarSign,
          color: '#10b981'
        }
      ];
    } else if (user?.role === 'security') {
      return [
        { label: 'Active Visitor Passes', value: stats.active_visitor_passes || 0, icon: QrCode, color: '#8b5cf6' },
        { label: 'Pending Parcels', value: stats.pending_parcels || 0, icon: Package, color: '#ec4899' }
      ];
    }
    return [];
  };

  return (
    <div className="space-y-8" data-testid="dashboard-container">
      {/* Welcome Header */}
      <div>
        <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>
          Welcome back, {user?.name}
        </h1>
        <p className="text-sm text-[#64748b]">
          {user?.role === 'resident' && 'Manage your home and connect with your community'}
          {user?.role === 'management' && 'Manage properties and serve your community'}
          {user?.role === 'security' && 'Monitor access and ensure safety'}
          {user?.role === 'owner' && 'Track your investments and properties'}
        </p>
      </div>

      {/* Push notifications toggle (per-device) */}
      <NotificationsToggle />

      {/* Management: prominent shortcut to Payment Approvals when any are pending.
          Same amber styling as the banner on /invoices — one-click into the
          dedicated approvals page. */}
      {user?.role === 'management' && pendingApprovalsCount > 0 && (
        <Card
          className="p-4 bg-amber-50 border border-amber-200 flex flex-wrap items-center justify-between gap-3 cursor-pointer hover:shadow-sm transition-shadow"
          onClick={() => navigate('/payment-approvals')}
          data-testid="dashboard-approvals-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Inbox className="h-5 w-5 text-amber-700" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900" style={{ fontFamily: 'Outfit' }}>
                {pendingApprovalsCount} payment approval{pendingApprovalsCount === 1 ? '' : 's'} awaiting your review
              </p>
              <p className="text-xs text-amber-800/80">
                Bank-transfer submissions from residents — approve or reject to update invoice balances.
              </p>
            </div>
          </div>
          <Button
            onClick={(e) => { e.stopPropagation(); navigate('/payment-approvals'); }}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="dashboard-open-approvals-button"
          >
            Open Payment Approvals <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Card>
      )}

      {/* Management: banner surfacing open facility bookings (pending + approved,
          i.e. all non-completed). Same UX as Payment Approvals for consistency —
          click anywhere on the card, or the button, to jump into /facilities. */}
      {user?.role === 'management' && (stats.open_bookings || 0) > 0 && (
        <Card
          className="p-4 bg-blue-50 border border-blue-200 flex flex-wrap items-center justify-between gap-3 cursor-pointer hover:shadow-sm transition-shadow"
          onClick={() => navigate('/facilities')}
          data-testid="dashboard-bookings-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Calendar className="h-5 w-5 text-blue-700" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-blue-900" style={{ fontFamily: 'Outfit' }}>
                {stats.open_bookings} facility booking{stats.open_bookings === 1 ? '' : 's'} to review
              </p>
              <p className="text-xs text-blue-800/80">
                Pending &amp; approved bookings not yet completed — approve, reject, or track upcoming reservations.
              </p>
            </div>
          </div>
          <Button
            onClick={(e) => { e.stopPropagation(); navigate('/facilities'); }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="dashboard-open-bookings-button"
          >
            Open Facility Bookings <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Card>
      )}

      {/* Management: banner surfacing open maintenance requests (anything not
          resolved/cancelled). Orange to match the "Wrench" iconography used
          throughout the app for maintenance. */}
      {user?.role === 'management' && (stats.open_maintenance || 0) > 0 && (
        <Card
          className="p-4 bg-orange-50 border border-orange-200 flex flex-wrap items-center justify-between gap-3 cursor-pointer hover:shadow-sm transition-shadow"
          onClick={() => navigate('/maintenance')}
          data-testid="dashboard-maintenance-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-md bg-orange-100 flex items-center justify-center flex-shrink-0">
              <Wrench className="h-5 w-5 text-orange-700" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-orange-900" style={{ fontFamily: 'Outfit' }}>
                {stats.open_maintenance} maintenance request{stats.open_maintenance === 1 ? '' : 's'} in progress
              </p>
              <p className="text-xs text-orange-800/80">
                Open &amp; in-progress requests from residents — assign, forward, or mark them resolved.
              </p>
            </div>
          </div>
          <Button
            onClick={(e) => { e.stopPropagation(); navigate('/maintenance'); }}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="dashboard-open-maintenance-button"
          >
            Open Maintenance <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Card>
      )}

      {/* Management: red banner when any resident is past due. One click emails
          all overdue residents a payment reminder via the same Gmail SMTP used
          by monthly statements. Idempotent — safe to click again to re-send. */}
      {user?.role === 'management' && overdueSummary.overdue_resident_count > 0 && (
        <Card
          className="p-4 bg-red-50 border border-red-200 flex flex-wrap items-center justify-between gap-3"
          data-testid="dashboard-overdue-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-md bg-red-100 flex items-center justify-center flex-shrink-0">
              <MailWarning className="h-5 w-5 text-red-700" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-red-900" style={{ fontFamily: 'Outfit' }}>
                {overdueSummary.overdue_resident_count} resident{overdueSummary.overdue_resident_count === 1 ? '' : 's'} past due
                {' · '}
                LKR {(overdueSummary.total_overdue_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} overdue
                {' across '}{overdueSummary.overdue_invoice_count} invoice{overdueSummary.overdue_invoice_count === 1 ? '' : 's'}
              </p>
              <p className="text-xs text-red-800/80">
                Send a payment-due reminder email to everyone whose due date has passed. Late fees continue to accrue until settled.
              </p>
            </div>
          </div>
          <Button
            onClick={openReminderModal}
            disabled={sendingReminders || reminderLoading}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="dashboard-send-reminders-button"
          >
            <MailWarning className="mr-2 h-4 w-4" />
            {sendingReminders ? 'Sending…' : (reminderLoading ? 'Loading…' : 'Send Overdue Reminders')}
          </Button>
        </Card>
      )}

      {/* Preview recipients modal — appears when admin clicks "Send Overdue
          Reminders". Renders a scrollable checklist so specific residents
          can be excluded from the batch. Residents without an email on file
          are shown greyed-out and cannot be selected. */}
      {reminderModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => (!sendingReminders && setReminderModalOpen(false))}
          data-testid="reminder-recipients-overlay"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-[#e2e8f0]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                    Preview overdue reminder recipients
                  </h3>
                  <p className="text-xs text-[#64748b] mt-1">
                    Uncheck any resident you don't want to email. Sending will re-notify anyone still overdue.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setReminderModalOpen(false)}
                  className="text-[#64748b] hover:text-[#0f172a] text-sm"
                  disabled={sendingReminders}
                  data-testid="close-reminder-modal"
                >
                  Close
                </button>
              </div>
              {!reminderLoading && reminderRecipients.length > 0 && (
                <div className="flex items-center justify-between mt-3">
                  <p className="text-xs text-[#475569]">
                    <strong className="text-[#0f172a]">{selectedRecipients.size}</strong>
                    {' of '}{reminderRecipients.filter((r) => r.has_email).length}
                    {' selected'}
                    {reminderRecipients.some((r) => !r.has_email) && (
                      <> · <span className="text-[#b91c1c]">{reminderRecipients.filter((r) => !r.has_email).length} without email</span></>
                    )}
                  </p>
                  <div className="flex gap-2 text-xs">
                    <button
                      type="button"
                      onClick={selectAllRecipients}
                      className="text-[#2563eb] hover:underline"
                      disabled={sendingReminders}
                      data-testid="select-all-recipients"
                    >
                      Select all
                    </button>
                    <span className="text-[#cbd5e1]">|</span>
                    <button
                      type="button"
                      onClick={clearAllRecipients}
                      className="text-[#2563eb] hover:underline"
                      disabled={sendingReminders}
                      data-testid="clear-all-recipients"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {reminderLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto mb-3" />
                  <p className="text-sm text-[#64748b]">Loading recipients…</p>
                </div>
              ) : reminderRecipients.length === 0 ? (
                <p className="text-sm text-[#64748b] py-8 text-center">No overdue residents.</p>
              ) : (
                <ul className="divide-y divide-[#f1f5f9]">
                  {reminderRecipients.map((r) => {
                    const selected = selectedRecipients.has(r.resident_id);
                    const disabled = !r.has_email || sendingReminders;
                    return (
                      <li
                        key={r.resident_id}
                        className={`flex items-start gap-3 py-2 px-2 rounded ${
                          !r.has_email ? 'bg-[#fef2f2] opacity-60' : 'hover:bg-[#f8fafc] cursor-pointer'
                        }`}
                        onClick={() => !disabled && toggleRecipient(r.resident_id)}
                        data-testid={`recipient-row-${r.resident_id}`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 accent-red-600 flex-shrink-0"
                          checked={selected}
                          disabled={disabled}
                          onChange={() => !disabled && toggleRecipient(r.resident_id)}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`recipient-checkbox-${r.resident_id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#0f172a] truncate">
                            {r.name}
                            {r.unit_number && <span className="text-[#64748b] font-normal"> · Unit {r.unit_number}</span>}
                          </p>
                          <p className="text-xs text-[#64748b] truncate">
                            {r.property_name || '—'}
                            {r.email ? <> · {r.email}</> : <> · <span className="text-[#b91c1c] font-medium">no email on file</span></>}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-semibold text-[#ef4444]" style={{ fontFamily: 'Outfit' }}>
                            LKR {r.total_due.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-[#64748b]">
                            {r.overdue_count} inv · {r.days_overdue_max}d overdue
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="p-4 border-t border-[#e2e8f0] flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setReminderModalOpen(false)}
                disabled={sendingReminders}
                className="border-[#e2e8f0]"
                data-testid="cancel-send-reminders"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendReminders}
                disabled={sendingReminders || selectedRecipients.size === 0}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-send-reminders"
              >
                <MailWarning className="mr-2 h-4 w-4" />
                {sendingReminders
                  ? 'Sending…'
                  : `Send to ${selectedRecipients.size} recipient${selectedRecipients.size === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-white rounded-lg border border-[#e2e8f0] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {getStatsCards().map((stat, index) => (
            <Card
              key={stat.label}
              className="p-6 bg-white border border-[#e2e8f0] hover:-translate-y-1 transition-transform duration-200"
              data-testid={`stat-card-${index}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mb-2">{stat.label}</p>
                  <p className="text-2xl font-semibold" style={{ fontFamily: 'Outfit' }}>{stat.value}</p>
                  {stat.sublabel && (
                    <p className="text-xs text-[#64748b] mt-1" data-testid={`stat-card-${index}-sublabel`}>{stat.sublabel}</p>
                  )}
                </div>
                <div
                  className="h-12 w-12 rounded-md flex items-center justify-center"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <stat.icon className="h-6 w-6" style={{ color: stat.color }} />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-2xl font-medium tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {getQuickActions().map((action, index) => (
            <Button
              key={action.path}
              onClick={() => navigate(action.path)}
              variant="outline"
              className="h-auto p-6 justify-start gap-4 bg-white hover:bg-[#f8fafc] border-[#e2e8f0] hover:border-[#2563eb] hover:scale-[1.02] transition-all duration-200"
              data-testid={`quick-action-${index}`}
            >
              <div
                className="h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${action.color}20` }}
              >
                <action.icon className="h-5 w-5" style={{ color: action.color }} />
              </div>
              <span className="font-medium text-sm">{action.label}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Outstanding breakdown - management only */}
      {user?.role === 'management' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="outstanding-breakdown-section">
          {/* By property */}
          <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid="outstanding-by-property-card">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-md bg-[#fef3c7] flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-5 w-5 text-[#d97706]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Outstanding by Property</h3>
                  <p className="text-xs text-[#64748b]">Maintenance, water and common utilities unpaid, per property</p>
                </div>
              </div>
              {breakdown.by_property.length > 0 && (() => {
                const anyExpanded = expandedProperties.size > 0;
                return (
                  <button
                    type="button"
                    onClick={anyExpanded ? collapseAllProperties : expandAllProperties}
                    className="text-xs font-semibold text-[#2563eb] hover:underline whitespace-nowrap flex-shrink-0"
                    data-testid="toggle-all-properties-btn"
                  >
                    {anyExpanded ? 'Collapse all' : 'Expand all'}
                  </button>
                );
              })()}
            </div>
            {breakdown.by_property.length === 0 ? (
              <p className="text-sm text-[#64748b] py-8 text-center" data-testid="no-outstanding-property">All properties are fully collected 🎉</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-[#e2e8f0]">
                      <th className="py-2 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]" style={{ width: '28px' }}></th>
                      <th className="py-2 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property</th>
                      <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Maintenance</th>
                      <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Water</th>
                      <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right whitespace-nowrap">Common Water</th>
                      <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right whitespace-nowrap">Common Electricity</th>
                      <th className="py-2 pl-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.by_property.map((p) => {
                      const isExpanded = expandedProperties.has(p.property_id);
                      const residentsForProperty = (breakdown.by_resident || [])
                        .filter((r) => r.property_id === p.property_id)
                        .sort((a, b) => b.total_outstanding - a.total_outstanding);
                      return (
                        <React.Fragment key={p.property_id}>
                          <tr
                            className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc] cursor-pointer"
                            data-testid={`property-outstanding-row-${p.property_id}`}
                            onClick={() => togglePropertyExpand(p.property_id)}
                          >
                            <td className="py-2 pr-2">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); togglePropertyExpand(p.property_id); }}
                                aria-label={isExpanded ? 'Collapse residents' : 'Expand residents'}
                                className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-[#e2e8f0] text-[#475569]"
                                data-testid={`property-expand-toggle-${p.property_id}`}
                              >
                                {isExpanded
                                  ? <ChevronDown className="h-4 w-4" />
                                  : <ChevronRight className="h-4 w-4" />}
                              </button>
                            </td>
                            <td className="py-2 pr-2">
                              <p className="text-sm font-medium truncate">{p.property_name}</p>
                              <p className="text-xs text-[#64748b]">
                                {p.invoice_count} invoice{p.invoice_count !== 1 ? 's' : ''}
                                {residentsForProperty.length > 0 && (
                                  <> · <span className="text-[#2563eb]">{residentsForProperty.length} resident{residentsForProperty.length !== 1 ? 's' : ''}</span></>
                                )}
                              </p>
                            </td>
                            <td className="py-2 px-2 text-right">
                              <p className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }} data-testid={`property-maintenance-${p.property_id}`}>{formatLKR(p.maintenance_outstanding)}</p>
                              <p className="text-xs text-[#64748b]">{p.maintenance_count} inv</p>
                            </td>
                            <td className="py-2 px-2 text-right">
                              <p className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }} data-testid={`property-water-${p.property_id}`}>{formatLKR(p.water_outstanding)}</p>
                              <p className="text-xs text-[#64748b]">{p.water_count} inv</p>
                            </td>
                            <td className="py-2 px-2 text-right">
                              <p className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }} data-testid={`property-common-water-${p.property_id}`}>{formatLKR(p.common_water_outstanding || 0)}</p>
                              <p className="text-xs text-[#64748b]">{p.common_water_count || 0} inv</p>
                            </td>
                            <td className="py-2 px-2 text-right">
                              <p className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }} data-testid={`property-common-electricity-${p.property_id}`}>{formatLKR(p.common_electricity_outstanding || 0)}</p>
                              <p className="text-xs text-[#64748b]">{p.common_electricity_count || 0} inv</p>
                            </td>
                            <td className="py-2 pl-2 text-right">
                              <p className="text-sm font-semibold text-[#ef4444]" style={{ fontFamily: 'Outfit' }}>{formatLKR(p.total_outstanding)}</p>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]" data-testid={`property-drilldown-${p.property_id}`}>
                              <td></td>
                              <td colSpan={6} className="py-2 pr-2">
                                {residentsForProperty.length === 0 ? (
                                  <p className="text-xs text-[#64748b] py-2">No resident-level outstanding — the outstanding balance may be at property level.</p>
                                ) : (() => {
                                  const showAll = showAllResidentsFor.has(p.property_id);
                                  const capped = !showAll && residentsForProperty.length > DRILLDOWN_ROW_CAP;
                                  const visibleRows = capped
                                    ? residentsForProperty.slice(0, DRILLDOWN_ROW_CAP)
                                    : residentsForProperty;
                                  const hiddenCount = residentsForProperty.length - visibleRows.length;
                                  return (
                                    <div className="space-y-1 py-2">
                                      <p className="text-[11px] font-semibold uppercase tracking-widest text-[#64748b] mb-1">
                                        Contributing residents ({residentsForProperty.length})
                                      </p>
                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr className="text-left">
                                            <th className="py-1 pr-2 font-medium text-[#64748b]">Resident</th>
                                            <th className="py-1 px-2 font-medium text-[#64748b] text-right">Maint.</th>
                                            <th className="py-1 px-2 font-medium text-[#64748b] text-right">Water</th>
                                            <th className="py-1 px-2 font-medium text-[#64748b] text-right whitespace-nowrap">Common Water</th>
                                            <th className="py-1 px-2 font-medium text-[#64748b] text-right whitespace-nowrap">Common Elec.</th>
                                            <th className="py-1 pl-2 font-medium text-[#64748b] text-right">Total</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {visibleRows.map((r) => (
                                            <tr key={r.resident_id} className="border-t border-[#e2e8f0]" data-testid={`property-drilldown-row-${p.property_id}-${r.resident_id}`}>
                                              <td className="py-1 pr-2">
                                                <span className="text-[#0f172a] font-medium">{r.resident_name}</span>
                                                {r.unit_number && <span className="text-[#64748b]"> · Unit {r.unit_number}</span>}
                                              </td>
                                              <td className="py-1 px-2 text-right text-[#0f172a]">{formatLKR(r.maintenance_outstanding)}</td>
                                              <td className="py-1 px-2 text-right text-[#0f172a]">{formatLKR(r.water_outstanding)}</td>
                                              <td className="py-1 px-2 text-right text-[#0f172a]">{formatLKR(r.common_water_outstanding || 0)}</td>
                                              <td className="py-1 px-2 text-right text-[#0f172a]">{formatLKR(r.common_electricity_outstanding || 0)}</td>
                                              <td className="py-1 pl-2 text-right font-semibold text-[#ef4444]">{formatLKR(r.total_outstanding)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                      {residentsForProperty.length > DRILLDOWN_ROW_CAP && (
                                        <div className="pt-2">
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); toggleShowAllResidents(p.property_id); }}
                                            className="text-xs font-semibold text-[#2563eb] hover:underline"
                                            data-testid={`show-all-residents-${p.property_id}`}
                                          >
                                            {showAll
                                              ? `Show top ${DRILLDOWN_ROW_CAP} only`
                                              : `See all ${residentsForProperty.length} residents (${hiddenCount} more)`}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <tr className="border-t-2 border-[#e2e8f0]" data-testid="total-outstanding-row">
                      <td></td>
                      <td className="py-3 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Total</td>
                      <td className="py-3 px-2 text-right text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }}>{formatLKR(breakdown.total_maintenance_outstanding)}</td>
                      <td className="py-3 px-2 text-right text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }}>{formatLKR(breakdown.total_water_outstanding)}</td>
                      <td className="py-3 px-2 text-right text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }}>{formatLKR(breakdown.total_common_water_outstanding || 0)}</td>
                      <td className="py-3 px-2 text-right text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }}>{formatLKR(breakdown.total_common_electricity_outstanding || 0)}</td>
                      <td className="py-3 pl-2 text-right text-base font-semibold text-[#ef4444]" style={{ fontFamily: 'Outfit' }}>{formatLKR(breakdown.total_outstanding)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* By resident */}
          <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid="outstanding-by-resident-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-md bg-[#fee2e2] flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-[#ef4444]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Outstanding by Resident</h3>
                <p className="text-xs text-[#64748b]">Maintenance, water and common utilities unpaid, per resident (highest first)</p>
              </div>
            </div>
            {breakdown.by_resident.length === 0 ? (
              <p className="text-sm text-[#64748b] py-8 text-center" data-testid="no-outstanding-resident">No residents have outstanding balances.</p>
            ) : (
              <div className="max-h-96 overflow-y-auto pr-1">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-left border-b border-[#e2e8f0]">
                      <th className="py-2 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Resident</th>
                      <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Maintenance</th>
                      <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Water</th>
                      <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right whitespace-nowrap">Common Water</th>
                      <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right whitespace-nowrap">Common Electricity</th>
                      <th className="py-2 pl-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.by_resident.map((r) => (
                      <tr key={r.resident_id} className="border-b border-[#f1f5f9] last:border-0" data-testid={`resident-outstanding-row-${r.resident_id}`}>
                        <td className="py-2 pr-2">
                          <p className="text-sm font-medium truncate">{r.resident_name}</p>
                          <p className="text-xs text-[#64748b] truncate">
                            {r.unit_number ? `Unit ${r.unit_number} · ` : ''}{r.property_name}
                          </p>
                        </td>
                        <td className="py-2 px-2 text-right">
                          <p className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }} data-testid={`resident-maintenance-${r.resident_id}`}>{formatLKR(r.maintenance_outstanding)}</p>
                          <p className="text-xs text-[#64748b]">{r.maintenance_count} inv</p>
                        </td>
                        <td className="py-2 px-2 text-right">
                          <p className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }} data-testid={`resident-water-${r.resident_id}`}>{formatLKR(r.water_outstanding)}</p>
                          <p className="text-xs text-[#64748b]">{r.water_count} inv</p>
                        </td>
                        <td className="py-2 px-2 text-right">
                          <p className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }} data-testid={`resident-common-water-${r.resident_id}`}>{formatLKR(r.common_water_outstanding || 0)}</p>
                          <p className="text-xs text-[#64748b]">{r.common_water_count || 0} inv</p>
                        </td>
                        <td className="py-2 px-2 text-right">
                          <p className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }} data-testid={`resident-common-electricity-${r.resident_id}`}>{formatLKR(r.common_electricity_outstanding || 0)}</p>
                          <p className="text-xs text-[#64748b]">{r.common_electricity_count || 0} inv</p>
                        </td>
                        <td className="py-2 pl-2 text-right">
                          <p className="text-sm font-semibold text-[#ef4444]" style={{ fontFamily: 'Outfit' }}>{formatLKR(r.total_outstanding)}</p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Recent Activity placeholder */}
      <div>
        <h2 className="text-2xl font-medium tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>Recent Activity</h2>
        <Card className="p-6 bg-white border border-[#e2e8f0]">
          <p className="text-sm text-[#64748b]">No recent activity to display</p>
        </Card>
      </div>
    </div>
  );
};
